import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, HelpCircle, MessageCircle, Video, FileText, Clock, User, ArrowRight, Bug, Play } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState, useMemo, useEffect } from "react";
import { useSiteConfigValue } from "@/hooks/useSiteConfigValue";
import DemoModal from "@/components/DemoModal";
import SEO from "@/components/SEO";
import { createBreadcrumbSchema } from "@/components/SEO";
const HelpCenter = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const {
    value: bugReportUrl,
    loading: bugReportLoading,
    refetch
  } = useSiteConfigValue('bug_report_form_url');
  const {
    value: demoVideoUrl
  } = useSiteConfigValue('demo_video_url', 'https://www.youtube.com/watch?v=dQw4w9WgXcQ');
  const {
    value: demoVideoTitle
  } = useSiteConfigValue('demo_video_title', 'Product Demo');

  // Debug logging (removed infinite refetch loop)
  useEffect(() => {
    console.log('HelpCenter - Bug report URL from config:', bugReportUrl);
    console.log('HelpCenter - Bug report loading:', bugReportLoading);
  }, [bugReportUrl, bugReportLoading]);
  const helpCategories = [{
    icon: HelpCircle,
    title: "Frequently Asked Questions",
    description: "Find answers to common questions about our platform",
    color: "text-blue-400",
    keywords: ["faq", "questions", "answers", "common", "help"],
    link: "/help/faq"
  }, {
    icon: Video,
    title: "Video Tutorials",
    description: "Step-by-step video guides to help you get started",
    color: "text-purple-400",
    keywords: ["video", "tutorials", "guides", "step-by-step", "started"],
    link: "/help/videos"
  }, {
    icon: FileText,
    title: "User Guides",
    description: "Detailed documentation and best practices",
    color: "text-green-400",
    keywords: ["guides", "documentation", "docs", "practices", "manual"],
    link: "/blog"
  }, {
    icon: MessageCircle,
    title: "Community Forum",
    description: "Connect with other users and share experiences",
    color: "text-yellow-400",
    keywords: ["community", "forum", "users", "connect", "share", "experiences"],
    link: "/help/community"
  }];
  const latestBlogPosts = [{
    id: "getting-started-ai-image-generation",
    title: "Getting Started with Bulk Image Generator",
    excerpt: "Learn how to create stunning AI-generated images in bulk with our comprehensive guide.",
    author: "Team BulkGen",
    date: "2024-12-15",
    readTime: "5 min read",
    category: "Getting Started",
    keywords: ["getting started", "bulk", "image", "generator", "ai", "guide"]
  }, {
    id: "advanced-prompting-techniques",
    title: "Advanced Prompting Techniques for Better Results",
    excerpt: "Master the art of writing effective prompts to get exactly the images you want from AI models.",
    author: "AI Expert",
    date: "2024-12-12",
    readTime: "7 min read",
    category: "Tips & Tricks",
    keywords: ["prompting", "techniques", "advanced", "ai", "models", "tips"]
  }, {
    id: "choosing-right-ai-model",
    title: "Choosing the Right AI Model for Your Project",
    excerpt: "Compare different AI models available on our platform and learn when to use each one for optimal results.",
    author: "Tech Team",
    date: "2024-12-10",
    readTime: "6 min read",
    category: "Technical",
    keywords: ["ai", "model", "choosing", "compare", "platform", "technical"]
  }];
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return helpCategories;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return helpCategories.filter(category => category.title.toLowerCase().includes(lowerSearchTerm) || category.description.toLowerCase().includes(lowerSearchTerm) || category.keywords.some(keyword => keyword.includes(lowerSearchTerm)));
  }, [searchTerm]);
  const filteredBlogPosts = useMemo(() => {
    if (!searchTerm.trim()) return latestBlogPosts;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return latestBlogPosts.filter(post => post.title.toLowerCase().includes(lowerSearchTerm) || post.excerpt.toLowerCase().includes(lowerSearchTerm) || post.category.toLowerCase().includes(lowerSearchTerm) || post.keywords.some(keyword => keyword.includes(lowerSearchTerm)));
  }, [searchTerm]);
  const hasResults = filteredCategories.length > 0 || filteredBlogPosts.length > 0;
  const handleBugReport = () => {
    // Use the configured URL, fallback to default if not set
    const url = bugReportUrl && bugReportUrl.trim() !== '' ? bugReportUrl : 'https://forms.gle/vwv1nkkQSvSj8ts49';
    console.log('HelpCenter - Opening bug report URL:', url);
    console.log('HelpCenter - Raw bugReportUrl value:', bugReportUrl);
    window.open(url, '_blank');
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SEO
        title="Help Center | The Bulk Image Generator"
        description="Find answers to common questions, video tutorials, and guides for using The Bulk Image Generator. Get the support you need to create amazing AI images."
        keywords="help center, faq, support, tutorials, guides, bulk image generator help"
        url="/help"
        jsonLd={[
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Help Center', url: '/help' }
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Help Center',
            description: 'Find answers to common questions, video tutorials, and guides for using The Bulk Image Generator.',
            url: 'https://thebulkimagegenerator.com/help',
            isPartOf: { '@type': 'WebSite', name: 'The Bulk Image Generator', url: 'https://thebulkimagegenerator.com' }
          }
        ]}
      />
      <Header />
      
      <main className="pt-24">
        <div className="container mx-auto px-6 py-8 relative">
          {/* Action Buttons - Top Left */}
          <div className="absolute top-4 left-6 z-10 flex gap-3">
            
            <Button onClick={() => setDemoModalOpen(true)} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300" size="sm">
              <Play className="w-4 h-4 mr-2" />
              Watch Demo
            </Button>
          </div>

          {/* Bug Report Button - Top Right */}
          <div className="absolute top-4 right-6 z-50 pointer-events-auto">
            <Button onClick={handleBugReport} disabled={bugReportLoading} className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 pointer-events-auto" size="sm">
              <Bug className="w-4 h-4 mr-2" />
              Report Bug
            </Button>
          </div>

          <div className="text-center mb-8">
            <Badge className="mb-3 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              Help Center
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How can we <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">help you?</span>
            </h1>
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input placeholder="Search for help articles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-lg py-4" />
            </div>
          </div>
          

          {searchTerm && !hasResults && <div className="text-center mb-12">
              <Card className="bg-white/5 backdrop-blur-md border-white/10 max-w-md mx-auto">
                <CardContent className="p-8">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-semibold mb-2">No results found</h3>
                  <p className="text-gray-400">Try searching with different keywords or browse our categories below.</p>
                </CardContent>
              </Card>
            </div>}

          {filteredCategories.length > 0 && <>
              {searchTerm && <h2 className="text-2xl font-bold text-white mb-6">Help Categories</h2>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {filteredCategories.map((category, index) => <Link key={index} to={category.link}>
                    <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-300 cursor-pointer h-full">
                      <CardHeader>
                        <category.icon className={`w-8 h-8 ${category.color} mb-4`} />
                        <CardTitle className="text-white">{category.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300">{category.description}</p>
                      </CardContent>
                    </Card>
                  </Link>)}
              </div>
            </>}

          {filteredBlogPosts.length > 0 && <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">
                  {searchTerm ? 'Related Blog Posts' : 'Latest from Our Blog'}
                </CardTitle>
                <Link to="/blog">
                  <Button variant="ghost" className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10">
                    View All Posts <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {filteredBlogPosts.map(post => <div key={post.id} className="p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-2">
                            <Badge variant="outline" className="border-blue-500/30 text-blue-300">
                              {post.category}
                            </Badge>
                            <div className="flex items-center text-gray-400 text-sm">
                              <Clock className="w-4 h-4 mr-1" />
                              {post.readTime}
                            </div>
                          </div>
                          <Link to={`/blog/${post.id}`}>
                            <h3 className="text-lg font-semibold text-white mb-2 hover:text-blue-400 transition-colors cursor-pointer">
                              {post.title}
                            </h3>
                          </Link>
                          <p className="text-gray-300 mb-3">{post.excerpt}</p>
                          <div className="flex items-center text-gray-400 text-sm">
                            <User className="w-4 h-4 mr-1" />
                            {post.author} • {post.date}
                          </div>
                        </div>
                        <Link to={`/blog/${post.id}`}>
                          <Button variant="ghost" className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 ml-4">
                            Read More
                          </Button>
                        </Link>
                      </div>
                    </div>)}
                </div>
                
                <div className="mt-6 text-center">
                  <Link to="/blog">
                    <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      Explore All Blog Posts
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>}
        </div>
      </main>

      <Footer />
      
      {/* Demo Modal */}
      <DemoModal open={demoModalOpen} onOpenChange={setDemoModalOpen} videoUrl={demoVideoUrl} title={demoVideoTitle} />
    </div>;
};
export default HelpCenter;